import type { ItemNormalizedType, LinkedItemSon, SceneObjects } from '@types'

export type KitchenApplianceTag =
  | 'sink'
  | 'oven'
  | 'dishwasher'
  | 'cooktop'
  | 'hood'
  | 'fridge'
  | 'cocina'

export type ApplianceKind = KitchenApplianceTag | 'cabinet' | 'island' | 'unknown'

export function textOf(item: SceneObjects): string {
  const parts = [
    typeof item.description === 'string' ? item.description : '',
    'typeOfSon' in item ? (item as LinkedItemSon).typeOfSon ?? '' : '',
    'long' in item && typeof item.long === 'string' ? item.long : '',
    'referencia' in item && typeof item.referencia === 'string' ? item.referencia : '',
    'catalogID' in item && item.catalogID != null ? String(item.catalogID) : '',
  ]
  return parts.join(' ').toLowerCase()
}

/** Kit alto campana: catálogo KAC / KACB (también "PE KACB…"). */
export function isHoodCatalogId(item: SceneObjects): boolean {
  const codes = [
    'catalogID' in item && item.catalogID != null ? String(item.catalogID) : '',
    'referencia' in item && typeof item.referencia === 'string' ? item.referencia : '',
    'codigo' in item && item.codigo != null ? String(item.codigo) : '',
  ]
    .map((value) => value.trim().toUpperCase().replace(/^PE\s+/, ''))
    .filter(Boolean)
  return codes.some((code) => code.startsWith('KAC'))
}

function linkedSonType(item: SceneObjects): string {
  if (item.type !== 'linkedSon') return ''
  return String((item as LinkedItemSon).typeOfSon ?? '').trim()
}

/** Elemento enlazado Placa / vitro / inducción. */
export function isPlacaItem(item: SceneObjects): boolean {
  const son = linkedSonType(item).toLowerCase()
  if (son === 'placa' || son.includes('placa')) return true
  const t = textOf(item)
  return (
    t.includes('placa') ||
    t.includes('vitro') ||
    t.includes('vitrocer') ||
    t.includes('inducc')
  )
}

/** Elemento enlazado Horno. */
export function isHornoItem(item: SceneObjects): boolean {
  const son = linkedSonType(item).toLowerCase()
  if (son === 'horno' || son === 'hornomicro' || son.includes('horno')) return true
  return textOf(item).includes('horno')
}

export function isHoodItem(item: SceneObjects): boolean {
  if (isHoodCatalogId(item)) return true
  const t = textOf(item)
  return t.includes('campana') || t.includes('extractor') || t.includes('extractora')
}

/** Electrodoméstico o módulo de cocina detectado en la unidad. */
export function getKitchenApplianceTag(item: SceneObjects): KitchenApplianceTag | null {
  const text = textOf(item)

  if (isHoodItem(item)) return 'hood'
  if (text.includes('fregadero')) return 'sink'
  if (text.includes('lavavajillas') || text.includes('lavaplatos')) return 'dishwasher'
  if (isHornoItem(item)) return 'oven'
  if (isPlacaItem(item)) return 'cooktop'
  if (
    text.includes('frigo') ||
    text.includes('nevera') ||
    text.includes('refriger') ||
    text.includes('congel') ||
    text.includes('frigor')
  )
    return 'fridge'

  // Módulo / electrodoméstico "cocina" (no confundir con la estancia)
  if (
    text.includes(' cocina') ||
    text.startsWith('cocina') ||
    text.includes('cocina integr') ||
    text.includes('cocina gas') ||
    text.includes('cocina elec')
  ) {
    return 'cocina'
  }

  return null
}

export function classifyItem(item: SceneObjects): ApplianceKind {
  const tag = getKitchenApplianceTag(item)
  if (tag) return tag
  if (item.type === 'partition') return 'island'
  if (textOf(item).includes('isla')) return 'island'

  const tipo = 'tipoNormalizado' in item ? (item.tipoNormalizado as ItemNormalizedType) : undefined
  if (tipo === 'MBJ' || tipo === 'MAL' || tipo === 'COL') return 'cabinet'

  return 'unknown'
}

export function isInspectableItem(item: SceneObjects): boolean {
  if (!item || item.type === 'walls' || item.type === 'hiddenWall' || item.type === 'areas')
    return false
  if (item.type === 'lights') return false
  if ('visible' in item && item.visible === false) return false
  return (
    item.type === 'items' ||
    item.type === 'linkedItem' ||
    item.type === 'linkedSon' ||
    item.type === 'itemGroup' ||
    item.type === 'partition'
  )
}

/** Hay al menos una unidad dibujada (mueble, no solo paredes ni huecos). */
export function hasPlacedUnits(items: Map<string, SceneObjects>): boolean {
  let found = false
  items.forEach((item) => {
    if (found) return
    if ('visible' in item && item.visible === false) return
    if (
      item.type === 'linkedItem' ||
      item.type === 'items' ||
      item.type === 'itemGroup' ||
      item.type === 'partition'
    ) {
      found = true
    }
  })
  return found
}

/** Hay al menos un mueble o electrodoméstico de cocina en escena. */
export function isKitchenScene(items: Map<string, SceneObjects>): boolean {
  let hasKitchenSignal = false
  items.forEach((item) => {
    if (item.type === 'walls' || item.type === 'holes' || item.type === 'areas') return
    if ('visible' in item && item.visible === false) return
    if (getKitchenApplianceTag(item)) hasKitchenSignal = true
    if (
      (item.type === 'linkedItem' || item.type === 'items') &&
      'tipoNormalizado' in item &&
      (item.tipoNormalizado === 'MBJ' ||
        item.tipoNormalizado === 'MAL' ||
        item.tipoNormalizado === 'COL')
    ) {
      hasKitchenSignal = true
    }
  })
  return hasKitchenSignal
}
